import { Component, ChangeDetectionStrategy, Input, ViewEncapsulation } from '@angular/core';
import { Observable, combineLatest, filter, map } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

import { AppFacade } from '@app/app.facade';
import { BenefitStyle, UserBenefitResponse } from '@shared/benefits/entities/benefits';
import { GlobalFacade } from '@app/global.facade';
import { BenefitViewType } from '@shared/benefits/entities/view-type';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import { TzDatesHelper } from '@ux/helpers/tz-dates.helper';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { environment as ENV } from '@environment';
import { Countries } from '@app/modules/ux/entities/country.enum';

@Component({
  selector: 'app-benefit-cashback',
  templateUrl: './benefit-cashback.container.html',
  styleUrls: ['./benefit-cashback.container.sass'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BenefitCashbackContainer {

  @Input() productReference: number | string;
  @Input() totalPrice: number;
  @Input() currencyCode: string;
  @Input() benefitViewType = BenefitViewType.Default;
  @Input() staticCmsPercentage = 0;

  public readonly CARD_PRO_ROUTE = 'web/travel/icons';
  public readonly CARD_PRO_IMG = 'card-pro.svg';

  constructor(
    private readonly globalFacade: GlobalFacade,
    private readonly appFacade: AppFacade,
    private readonly translocoService: TranslocoService,
    private readonly currencyFormatPipe: CurrencyFormatPipe,
  ) { }

  private get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$;
  }

  private get isPrime$(): Observable<boolean> {
    return this.appFacade.isPrime$;
  }

  private get hasRappiAccount$(): Observable<boolean> {
    return this.appFacade.hasRappiAccount$;
  }

  get cashbackBenefit$(): Observable<UserBenefitResponse> {
    return combineLatest(
      [this.globalFacade.benefits$,
        this.hasRappiAccount$,
        this.hasRappiCard$,
        this.isPrime$
      ]
    ).pipe(
      filter(() => !!this.productReference),
      map(([benefits, hasRappiAccount, hasRappiCard, isPrime]) => benefits.find(benefit => (
        this.isValidProductReference(benefit.product_references) &&
        this.isValidTimePeriod(benefit.start_date, benefit.end_date) &&
        this.hasCashbackBenefit(benefit, hasRappiAccount, hasRappiCard, isPrime)
      ) || benefit.is_default && this.hasCashbackBenefit(benefit, hasRappiAccount, hasRappiCard, isPrime)
      ))
    )
  }
  

  get benefitStyle$(): Observable<BenefitStyle> {
    return combineLatest([
      this.cashbackBenefit$, this.isPrime$, this.hasRappiAccount$, this.hasRappiCard$]).pipe(
      map(([benefit, isPrime, hasRappiAccount, hasRappiCard]) => {
        const { rappi_credits_percentage, rappiaccount_cashback_percentage, rappicard_cashback_percentage,
          rappi_credits_min_value, rappicard_cashback_min_value, rappicard_cashback_cap, rappi_credits_cap, is_prime,
          rappiaccount_cashback_min_value
        } = benefit || {};
        switch (true) {
          case is_prime && 
          hasRappiCard && isPrime && rappicard_cashback_percentage > 0 && rappi_credits_percentage > 0 &&
          this.totalExceedsMinValue(rappicard_cashback_min_value)  && this.totalExceedsMinValue(rappi_credits_min_value):
            const rappiCardBenefit = 
              this.currencyFormatPipe.transform(
                this.cashbackBenefitValue(rappicard_cashback_percentage, rappicard_cashback_cap), 
                this.currencyCode);
            const rappiCreditsBenefit = 
              this.currencyFormatPipe.transform(
                this.cashbackBenefitValue(rappi_credits_percentage, rappi_credits_cap), 
                this.currencyCode);
            return this.hasCombinedCashbackOption ? BenefitStyle.Combined : rappiCardBenefit > rappiCreditsBenefit ?
              BenefitStyle.RappiCard : BenefitStyle.Prime;
          case hasRappiCard && rappicard_cashback_percentage > 0 && this.totalExceedsMinValue(rappicard_cashback_min_value):
            return BenefitStyle.RappiCard;
          case is_prime && isPrime && rappi_credits_percentage > 0  && this.totalExceedsMinValue(rappi_credits_min_value):
            return BenefitStyle.Prime;
          case hasRappiAccount && 
          rappiaccount_cashback_percentage > 0 && this.totalExceedsMinValue(rappiaccount_cashback_min_value):
            return BenefitStyle.RappiAccount;
          default:
            return null;
        }
      })
    )
  }

  get hasCombinedCashbackOption(): boolean {
    return ENV.countryInfo.iso_code === Countries.Mexico || ENV.countryInfo.iso_code === Countries.Chile; 
  }

  public benefitIcon(benefitStyle: BenefitStyle): Icon {
    return benefitStyle === BenefitStyle.Prime ? Icon.Prime : Icon.CreditCard;
  }

  public isCombined(benefitStyle: BenefitStyle): boolean {
    return benefitStyle === BenefitStyle.Combined;
  }

  public iconColor(benefitStyle: BenefitStyle): IconFillColor {
    switch(benefitStyle) {
      case BenefitStyle.RappiCard:
      case BenefitStyle.RappiAccount:
        return IconFillColor.Pay;
      default:
        return null;
    }
  }

  public cashbackBenefitLabel$(benefit: UserBenefitResponse, benefitStyle: BenefitStyle): Observable<string> {
    let additionalText;
    let percentage;
    let cap;
    let value;
    let rappiCardCashbackValue;
    let rappiCreditsCashbackValue;

    switch(benefitStyle) {
      case BenefitStyle.Combined:
        additionalText = benefit.rappicard_cashback_additional_text;
        rappiCardCashbackValue = 
          this.cashbackBenefitValue(benefit.rappicard_cashback_percentage, benefit.rappicard_cashback_cap);
        rappiCreditsCashbackValue = 
          this.cashbackBenefitValue(benefit.rappi_credits_percentage, benefit.rappi_credits_cap);
        value = this.currencyFormatPipe.transform((rappiCardCashbackValue + rappiCreditsCashbackValue), this.currencyCode);
        break;
      case BenefitStyle.RappiCard:
        additionalText = benefit.rappicard_cashback_additional_text;
        percentage = benefit.rappicard_cashback_percentage;
        cap = benefit.rappicard_cashback_cap;    
        break;
      case BenefitStyle.Prime:
        additionalText = benefit.rappi_credits_additional_text;
        percentage = benefit.rappi_credits_percentage;
        cap = benefit.rappi_credits_cap;
        break; 
      case BenefitStyle.RappiAccount:
        additionalText = benefit.rappiaccount_cashback_additional_text;
        percentage = benefit.rappiaccount_cashback_percentage;
        cap = benefit.rappiaccount_cashback_cap;
        break; 
    }
    const translationRoot = 'BENEFITS.CASHBACK.';
    const translateStart$ = this.translocoService.selectTranslate(`${translationRoot}START_WITH`);
    let translateEnd$: Observable<string>;
    
    if (benefitStyle === BenefitStyle.Combined) {
      translateEnd$ = this.translocoService.selectTranslate(
        `${translationRoot}${benefitStyle}`,
        {
          credits: this.currencyFormatPipe.transform(rappiCreditsCashbackValue, this.currencyCode),
          cashback: this.currencyFormatPipe.transform(rappiCardCashbackValue, this.currencyCode),
        }
      );
    } else {
      value = this.currencyFormatPipe.transform(this.cashbackBenefitValue(percentage, cap), this.currencyCode);
      translateEnd$ = this.translocoService.selectTranslate(`${translationRoot}${benefitStyle}`);
    }

    return combineLatest([translateStart$, translateEnd$]).pipe(
      map(([translateStart, translateEnd]) => {
        return `${translateStart} <span>${value}</span> ${translateEnd} ${additionalText}`;
      })
    );
  }

  private cashbackBenefitValue(percentage: number, cap: number): number {
    const value = (this.totalPrice * percentage) / 100;
    return cap === 0 ? value : value > cap ? cap : value;
  }

  private isValidProductReference(references: string[]): boolean {
    return references?.some(ref => {
      return ref === this.productReference;
    });
  }

  private isValidTimePeriod(startDate: string, endDate: string): boolean {
    return new TzDatesHelper().isTodayBetweenTwoDates(startDate, endDate);
  }

  private hasCashbackBenefit(
    benefit: UserBenefitResponse, hasRappiAccount: boolean, hasRappiCard: boolean, isPrime: boolean
  ): boolean {
    const { rappi_credits_percentage, rappiaccount_cashback_percentage, rappicard_cashback_percentage
    } = benefit;
    return (hasRappiCard && rappicard_cashback_percentage > 0) ||
    (hasRappiAccount && rappiaccount_cashback_percentage > 0) || 
    (isPrime && rappi_credits_percentage > 0);
  }

  private totalExceedsMinValue(minValue: number): boolean {
    return this.totalPrice > minValue;
  }
}
